import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../Button/Button";
import Flex from "../../Flex/Flex";
import IconSelect from "../../icons/IconSelect";
import style from "./Select.module.css";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import IconCheck from "../../icons/IconCheck";
import Flyout, { FlyoutAnchor, FlyoutPrefer } from "@/components/Flyout";

export const Divider = new (class Divider {})();

export type SelectProps<T> = Omit<MultiSelectProps<T>, "selectionRender"|"value"|"onChange"|"defaultOption"> & {
  selectionRender?: (option: T, index: number) => React.ReactNode; // the currently selected thing, defaults to same as 'optionRender'
  value?: T;
  onChange?: (option: T, index: number) => void;
  defaultOption?: T;
}

export interface MultiSelectProps<T> extends Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "onChange"> {
  optionRender?: (option: T, index: number) => React.ReactNode;    // options in the dropdown
  selectionRender?: (options: {option: T, index: number}[]) => React.ReactNode; // the currently selected thing, defaults to same as 'optionRender'
  value?: T[];
  onChange?: (options: {option: T, index: number}[]) => void;
  onClose?: () => void;
  onOptionClick?: (option: T, index: number, closeDropdown: () => void) => void;

  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;

  id?: string;
  placeholder?: React.ReactNode;

  options?: ReadonlyArray<T | typeof Divider>;
  optionStates?: boolean[];  // whether options are enabled/disabled
  defaultOption?: T[];

  fullWidth?: boolean;
  prefer?: FlyoutPrefer;
  anchor?: FlyoutAnchor;
  highlightSelected?: boolean; //Used when a batch only has one shipment.
  highlightSelectedClass?: string | ((option: T, index: number) => string | undefined);

  className?: string;
  size?: "small" | "medium" | "large";
}

export default function Select<T>(props: SelectProps<T>) {
  const {
    selectionRender = props.optionRender,
    value,
    onChange = () => null,
    defaultOption,
    ...rest
  } = props;
  return (
    <MultiSelect
      selectionRender={v => selectionRender?.(v[0].option, v[0].index)}
      onChange={v => onChange(v[0].option, v[0].index)}
      value={value == null ? [] : [value]}
      defaultOption={defaultOption == null ? [] : [defaultOption]}
      {...rest}
    />
  );
}

export function MultiSelect<T>(props: MultiSelectProps<T>) {
  const {
    optionRender,
    selectionRender,
    value,
    onChange,
    onClose,
    onOptionClick,
    defaultOption,
    loading = false,
    disabled = false,
    invalid = false,
    id,
    placeholder = "Select something",
    options = [],
    optionStates = [],
    fullWidth = false,
    anchor,
    prefer,
    highlightSelected = true,
    className,
    size,
    highlightSelectedClass = style.dropdownItemSelected,
    ...rest
  } = props;
  
  const _optionRender = optionRender ?? ((option: T, index: number): React.ReactNode => {
    return option.toString();
  });
  const _selectionRender = selectionRender ?? ((options: {option: T, index: number}[]): React.ReactNode => {
    return options.map(opt => opt.option).toString();
  });

  const [selectOpen, setSelectOpen] = useState<boolean>(false);
  const [closing, setClosing] = useState(false);
  const [minWidth, setMinWidth] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fHighlightSelectedClass = useMemo(() => (
    highlightSelectedClass instanceof Function ? highlightSelectedClass : () => highlightSelectedClass
  ), [highlightSelectedClass]);

  let displayOption = value;
  if (displayOption.length === 0 && defaultOption) displayOption = defaultOption;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!selectOpen) return;
      const outsideOfButton = buttonRef.current && !buttonRef.current.contains(e.target as Node)
      const outsideOfFlyout = dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      if (outsideOfButton && outsideOfFlyout) {
        setClosing(true);
      }
    };

    if (dropdownRef.current) {
      dropdownRef.current.addEventListener("animationend", () => {
        if (closing) {
          setSelectOpen(false);
          setClosing(false);
        }
      });
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef.current, selectOpen, closing]);

  useEffect(() => {
    if (selectOpen) {
      return;
    }
    if (dropdownRef.current) {
      // 10px = double the margin of dropdown items
      // need to update this if that value is changed
      // this rigamarole is necessary for the select button to be sized according to the content of the
      // flyout, as a native select would be. creates a bit of a two-way binding problem, because
      // the flyout wants to size itself according to the parent, and the button wants to size itself
      // according to the flyout contents. my solution is to force the flyout to have 0 width when closed
      // and then when opening check the scroll width. once open, it will be allowed to resize itself.
      setMinWidth(dropdownRef.current.scrollWidth - 10);
    }
  }, [selectOpen, dropdownRef.current?.scrollWidth]);

  return (
    <div
      id={id}
      className={`${style.select} ${fullWidth ? style.fullWidth : ''} ${className || ""}`}
      ref={buttonRef}
      {...rest}
    >
      <Button
        className={`${style.button} ${fullWidth ? style.fullWidth : ''} ${invalid ? style.invalid : ''}`}
        disabled={disabled || loading}
        type="secondary"
        size={size}
        onClick={(e) => {
          e.stopPropagation(); // so the event listener above doesn't fire.
          if (selectOpen) {
            setClosing(true);
          } else {
            setSelectOpen(true);
          }
        }}
      >
        {loading && <LoadingSpinner />}
        {!loading && (
          <Flex
            className={`${style.buttonContent} ${fullWidth ? style.fullWidth : ''}`}
            style={{minWidth: `${minWidth}px`}}
            justify="space-between"
            align="center"
            gap={2}
          >
            {displayOption.length === 0
              ? placeholder
              :  _selectionRender(displayOption.map(option => ({option, index: options.findIndex(o => o === option)})))
            }
            <IconSelect />
          </Flex>
        )}
      </Button>
      <Flyout
        className={`${style.dropdown} ${closing ? style.closing : ""} ${selectOpen ? style.open : ""}`}
        visible={selectOpen || closing}
        ref={dropdownRef}
        anchor={anchor}
        prefer={prefer}>
        {options.map((option, index) => (option === Divider
          ? <div key={index} style={{borderTop: "1px solid var(--static-divider-standard)"}} />
          : (
            <div 
              className={`
                ${optionStates[index] === undefined || optionStates[index] ? style.dropdownItem : style.dropdownDisabled}
                ${(displayOption.includes(option as T) && highlightSelected) ? fHighlightSelectedClass(option as T, index) ?? style.dropdownItemSelected : ''}
                ${!highlightSelected ? style.noHover : ""}
              `}
              key={index}
              onClick={onOptionClick
                ? () => onOptionClick(option as T, index, () => setClosing(true))
                : (e => {
                  setClosing(true);
                  if (optionStates[index] === undefined || optionStates[index]) {
                    onChange?.([{option: option as T, index}]);
                  }
                  e.preventDefault();
                  e.stopPropagation();
                })
              }
            >
              {_optionRender(option as T, index)}
              {(option === displayOption && highlightSelected) && <Flex align="center" style={{color:"var(--primary-enabled, #ff5823)"}}><IconCheck/></Flex>}
            </div>
          )
        ))}
      </Flyout>
    </div>
  );
};
